import { LocalDate } from './local-date';
import { Nullable } from 'src/core/common/domain/types/types';

/**
 * The short name shown next to a task's hours when its clock is not the device's, so a list ordered
 * by the real instant does not look mis-sorted.
 *
 * Users read "Lisboa", not "Europe/Lisbon", so the zones the app operates in are named here. ISS
 * Iberia works in mainland Spain and Portugal, the Canaries and Ceuta/Melilla, and those four zones
 * cover every task it can receive. Anything else falls back to the GMT offset, which is not pretty
 * but is never wrong — the fallback is a key with a parameter so the caller translates one thing,
 * never two.
 *
 * A new zone is one entry here plus one line in each i18n file, never a new branch.
 */
export class TimezoneHint {
  private static readonly TRANSLATION_KEYS: Record<string, string> = {
    'Africa/Ceuta': 'TIMEZONE.AFRICA_CEUTA',
    'Atlantic/Canary': 'TIMEZONE.ATLANTIC_CANARY',
    'Europe/Lisbon': 'TIMEZONE.EUROPE_LISBON',
    'Europe/Madrid': 'TIMEZONE.EUROPE_MADRID',
  };

  private static readonly GMT_OFFSET_KEY = 'TIMEZONE.GMT_OFFSET';

  private constructor(readonly key: string, readonly params: object = {}) {}

  static forDate(date: LocalDate): Nullable<TimezoneHint> {
    if (!date.readsDifferentlyOnDevice()) {
      return null;
    }

    const translationKey = TimezoneHint.TRANSLATION_KEYS[date.timezone];
    if (translationKey) {
      return new TimezoneHint(translationKey);
    }

    return new TimezoneHint(TimezoneHint.GMT_OFFSET_KEY, { offset: TimezoneHint.gmtOffsetOf(date) });
  }

  // '+01:00' reads as '+1' and '+05:30' as '+5:30': drop the minutes only when there are none, and
  // the padding zero always. Half-hour zones are real and must survive.
  private static gmtOffsetOf(date: LocalDate): string {
    return date
      .toFormat('Z')
      .replace(/:00$/, '')
      .replace(/^([+-])0/, '$1');
  }
}
